import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Image from 'components/Common/Image/Image'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import Caption from 'LuxKit/Typography/Caption'

const HAMBURGER_MENU_LUX_PLUS_IMAGE_ID = 'hamburguer-menu-luxplus'
const HAMBURGER_MENU_SOCIETE_IMAGE_ID = 'hamburguer-menu-societe'

/**
 * ClickableListItem applies a neutral hover background; restate product surfaces on :hover so rows stay static (no extra hover treatment).
 */
const LuxPlusListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
  min-height: ${rem(50)};
  background-color: ${props => props.theme.palette.product.luxPlus.background};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.L};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.product.luxPlus.background};
      }
    }
  }
`

const LoyaltyListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
  min-height: ${rem(50)};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
      }
    }
  }
`

const LoyaltyImageContainer = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

interface Props {
  isNew?: boolean
  variant: 'luxPlus' | 'loyalty'
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

function NavBarHamburgerMenuLoyaltyItem(props: Props) {
  const { variant, onClick } = props
  const intl = useIntl()

  const isLuxPlusMember = useAppSelector(state => getCanRedeemLuxPlusBenefits(state))
  const tier = useAppSelector(getLoyaltyDisplayTier)

  const luxPlusJoinText = intl.formatMessage({
    id: 'riiInfd4OuMemvufgOfd',
    defaultMessage: 'Join our VIP Travel Club',
    description: 'Text shown for non-members to join Lux Plus for the membership',
  })

  const luxPlusMemberText = intl.formatMessage({
    id: 'fasgrSyV_71UXvEDjPuU',
    defaultMessage: 'VIP Travel Club',
    description: 'Text shown for Lux Plus members in the navigation bar',
  })

  const societeText = intl.formatMessage({
    id: 'T_zzHmFx0EEkR0O0xBo5',
    defaultMessage: 'SOCIÉTÉ',
    description: 'Text shown for Société program in the loyalty program section',
  })

  const loyaltyText = intl.formatMessage({
    id: 'fubr0Nb5bY_i29Le2cQD',
    defaultMessage: 'Our free loyalty program',
    description: 'Text shown for loyalty program in the loyalty program section',
  })

  if (variant === 'luxPlus') {
    return (
      <LuxPlusListItem
        size="medium"
        startIcon={<LoyaltyImageContainer>
          <Image
            id={HAMBURGER_MENU_LUX_PLUS_IMAGE_ID}
            dpr={2}
            fit="center"
            objectFit="cover"
          />
        </LoyaltyImageContainer>}
        onClick={onClick}
        to="/lux-plus"
        titleLineClamp={0}
        endIcon={<LineAngleRightBIcon />}
        title={
          <div>
            <LuxPlusLogo colour="inherit" height={12} />
            <Caption variant="medium">{!isLuxPlusMember ? luxPlusJoinText : luxPlusMemberText}</Caption>
          </div>
        }
      />
    )
  }

  return (
    <ProductPaletteProvider palette={`tier-${tier}`}>
      <LoyaltyListItem
        className={`tier-${tier}`}
        size="medium"
        startIcon={<LoyaltyImageContainer>
          <Image
            id={HAMBURGER_MENU_SOCIETE_IMAGE_ID}
            dpr={2}
            fit="center"
            objectFit="cover"
          />
        </LoyaltyImageContainer>}
        onClick={onClick}
        to={`/${LOYALTY_LANDING_PAGE}`}
        titleLineClamp={0}
        overlineFormat="uppercase"
        endIcon={<LineAngleRightBIcon />}
        title={societeText}
        subtitle={loyaltyText}
        titleWeight="bold"
      />
    </ProductPaletteProvider>
  )
}

export default NavBarHamburgerMenuLoyaltyItem
